import { useMemo, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { getDay } from 'date-fns';
import timeaway from '../../../network/timeaway';
import classNames from 'classnames';
import Notification from '../../../ui/component/notification';
import { useLeaveTypes } from '../../../hooks/useLeaveTypes';

enum ETimeofError {
  OVERLAPPING_REQUESTS = 'OVERLAPPING_REQUESTS',
}

function isWeekday(date: Date): boolean {
  const day = getDay(date);
  return day !== 0 && day !== 6;
}

type BookTimeAwayProps = {
  onBooked?: () => void;
};

export default function BookTimeAway({ onBooked }: BookTimeAwayProps) {
  // The company's active leave types (the built-in list while loading).
  const { leaveTypes } = useLeaveTypes();
  const minDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [validationError, triggerValidationError] = useState(false);
  const [showNotification, setNotificationVisibility] = useState(false);
  const [showErrorTimeawayNotification, setShowErrorTimeawayNotification] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const buttonClassNames = classNames(
    'inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500':
        !validationError,
      'bg-gray-400 text-gray-700 cursor-not-allowed': validationError,
    },
  );

  return (
    <>
      <Formik
        initialValues={{
          kind: 'holiday_paid',
          note: '',
          start: new Date(),
          start_time: 'start_of_day',
          end: new Date(),
          end_time: 'midday',
        }}
        onSubmit={async (values, actions) => {
          try {
            const selected = leaveTypes.find((t) => t.key === values.kind);
            const wholeDays = selected && !selected.allows_part_days;
            await timeaway.bookTime(
              values.kind,
              {
                date: values.start,
                time: wholeDays ? 'start_of_day' : values.start_time,
              },
              {
                date: values.end,
                time: wholeDays ? 'end_of_day' : values.end_time,
              },
              values.note?.trim() || undefined,
            );
            setShowErrorTimeawayNotification(false);
            setNotificationVisibility(true);
            actions.resetForm();
            onBooked?.();
          } catch (error: any) {
            const status = error?.response?.data?.status;
            if (status === ETimeofError.OVERLAPPING_REQUESTS) {
              setErrorMessage('This time is already booked. Please choose another time.');
            } else if (status === 'INSUFFICIENT_BALANCE') {
              setErrorMessage('You do not have enough remaining balance for this leave type.');
            } else if (status === 'PAST_DATES_NOT_ALLOWED') {
              setErrorMessage('You cannot request time off in the past.');
            } else if (status === 'FUTURE_DATES_NOT_ALLOWED') {
              setErrorMessage('You cannot request time off more than 1 year in advance.');
            } else if (status === 'INVALID_DURATION') {
              setErrorMessage('This request has no working-day duration. Please choose weekday dates.');
            } else if (status === 'INVALID_LEAVE_TYPE') {
              setErrorMessage('Invalid leave type selected. Please choose another leave type.');
            } else if (status === 'NOTE_REQUIRED') {
              setErrorMessage('This leave type needs a note. Please add one and try again.');
            } else if (status === 'PART_DAYS_NOT_ALLOWED') {
              setErrorMessage('This leave type must be booked in whole days.');
            } else if (status === 'INVALID_TIME_OF_DAY') {
              setErrorMessage('Invalid time of day selected. Please refresh and try again.');
            } else {
              setErrorMessage('Unable to submit request. Please try again.');
            }
            setNotificationVisibility(false);
            setShowErrorTimeawayNotification(true);
          }
        }}
      >
        {({ values }) => {
          const selectedType = leaveTypes.find((t) => t.key === values.kind);
          const allowsPartDays = selectedType?.allows_part_days ?? true;
          const requiresNote = Boolean(selectedType?.requires_note);
          return (
          <Form>
            <div className="shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white px-4 py-5 sm:p-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="col-span-3 sm:col-span-2">
                    <label
                      htmlFor="company-website"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Time away type
                    </label>
                    <Field
                      as="select"
                      id="kind"
                      name="kind"
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                    >
                      {leaveTypes
                        .filter((type) => type.active)
                        .map((type) => (
                          <option key={type.key} value={type.key}>
                            {type.name}
                          </option>
                        ))}
                    </Field>
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Start date
                    </label>
                    <Field
                      type="date"
                      name="start"
                      id="start-date"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                      min={minDate}
                    >
                      {(props: any) => {
                        const { field } = props;
                        const selectedDate = new Date(field.value);
                        const day = getDay(selectedDate);
                        const isDisabled = !isWeekday(selectedDate);

                        // useEffect(() => {
                        //   if (typeof field.value !== "undefined") {
                        //     triggerValidationError(isDisabled);
                        //   }
                        // }, [field.value]);

                        return (
                          <div>
                            <input
                              {...field}
                              type="date"
                              min={minDate}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                            />
                            {isDisabled && (
                              <div className="mt-1 text-sm text-red-500">
                                {day === 0 ? 'Sunday' : 'Saturday'} is not a
                                valid day to book timeaway.
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </Field>
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Start time
                    </label>
                    <Field
                      as="select"
                      id="start-time"
                      name="start_time"
                      disabled={!allowsPartDays}
                      autoComplete="start-date"
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                    >
                      <option value="start_of_day">the start of the day</option>
                      <option value="midday">midday</option>
                    </Field>
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      End date
                    </label>
                    <Field
                      type="date"
                      id="end-date"
                      name="end"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                      min={values.start}
                    >
                      {(props: any) => {
                        const { field } = props;
                        const { value } = field;
                        const selectedDate = new Date(value);
                        const day = getDay(selectedDate);
                        const isDisabled = !isWeekday(selectedDate);
                        triggerValidationError(isDisabled);

                        return (
                          <div>
                            <input
                              {...field}
                              type="date"
                              min={minDate}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                            />
                            {isDisabled && (
                              <div className="mt-1 text-sm text-red-500">
                                {day === 0 ? 'Sunday' : 'Saturday'} is not a
                                valid day to book timeaway.
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </Field>
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      End time
                    </label>
                    <Field
                      as="select"
                      id="end-time"
                      name="end_time"
                      disabled={!allowsPartDays}
                      autoComplete="start-date"
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                    >
                      <option value="midday">midday</option>
                      <option value="end_of_day">the end of the day</option>
                    </Field>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="note"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {requiresNote ? 'Note (required)' : 'Note (optional)'}
                  </label>
                  <Field
                    as="textarea"
                    id="note"
                    name="note"
                    rows={2}
                    maxLength={500}
                    placeholder={
                      requiresNote
                        ? 'Add a reason or a link to a supporting document'
                        : ''
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                  />
                  {!allowsPartDays && (
                    <p className="mt-1 text-xs text-gray-500">
                      {selectedType?.name} is booked in whole days.
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 text-right sm:px-6">
                <button
                  type="submit"
                  className={buttonClassNames}
                  disabled={validationError || (requiresNote && !values.note?.trim())}
                >
                  Send request
                </button>
              </div>
            </div>
          </Form>
          );
        }}
      </Formik>
      <Notification
        title="Time off requested."
        message="Your request for time off has been successfully submitted."
        open={showNotification}
        close={() => setNotificationVisibility(false)}
      />
      <Notification
        title="Time off can not be requested."
        message={errorMessage}
        open={showErrorTimeawayNotification}
        close={() => setShowErrorTimeawayNotification(false)}
        isError={true}
      />
    </>
  );
}
